import type {
  AdminLessonListItemDto,
  AdminRosterGuardianSummaryDto,
  AdminRosterRelationsResponseDto,
  AdminStudentDto,
  EnrollmentDto,
  FamilyAccountDto,
  MemberStatus,
  StudentStatus
} from "@madamuh/domain";
import { buildAdminSearchHaystack } from "../../shared/lib/adminSearch";
import { GENERIC_TENANT_LABELS, type TenantLabels } from "../../shared/lib/tenantLabels";

export function buildAdminRosterLinks(labels: TenantLabels = GENERIC_TENANT_LABELS) {
  return [
  { to: "/t/:tenantSlug/teacher/admin/students", label: labels.student },
  { to: "/t/:tenantSlug/teacher/admin/family-accounts", label: "家族" },
  { to: "/t/:tenantSlug/teacher/admin/enrollments", label: labels.enrollment },
  { to: "/t/:tenantSlug/teacher/admin/guardian-links", label: "保護者紐付け" }
];
}

export const adminRosterLinks = buildAdminRosterLinks();

export const CURRENT_ENROLLMENT_STATUSES = ["pendingStart", "active", "paused"] as const;

export type AdminStudentListRow = {
  student: AdminStudentDto;
  familyAccount: FamilyAccountDto | null;
  siblingStudents: Array<{
    studentId: string;
    displayName: string;
    status: StudentStatus;
  }>;
  linkedGuardians: Array<
    AdminRosterGuardianSummaryDto & {
      isPrimaryContact: boolean;
    }
  >;
  primaryContact: {
    displayName: string;
    memberStatus: MemberStatus | null;
    hasActiveLinkToThisStudent: boolean;
  } | null;
  dataQualityWarnings: string[];
  currentEnrollments: EnrollmentDto[];
  currentLessonIds: string[];
  currentLessonTitles: string[];
  searchText: string;
};

export type AdminFamilyAccountListRow = {
  familyAccount: FamilyAccountDto;
  students: AdminStudentDto[];
  linkedGuardians: Array<
    AdminRosterGuardianSummaryDto & {
      isPrimaryContact: boolean;
    }
  >;
  primaryContact: {
    displayName: string;
    memberStatus: MemberStatus | null;
    hasActiveLinkToAnyFamilyStudent: boolean;
  } | null;
  dataQualityWarnings: string[];
  currentLessonIds: string[];
  currentLessonTitles: string[];
  searchText: string;
};

const collator = new Intl.Collator("ja", { numeric: true, sensitivity: "base" });

export function compareAdminText(left: string | null | undefined, right: string | null | undefined): number {
  const leftValue = left ?? "";
  const rightValue = right ?? "";
  if (!leftValue && rightValue) return 1;
  if (leftValue && !rightValue) return -1;
  return collator.compare(leftValue, rightValue);
}

export function compareAdminDate(left: string | null | undefined, right: string | null | undefined): number {
  const leftValue = left ?? "";
  const rightValue = right ?? "";
  if (!leftValue && rightValue) return 1;
  if (leftValue && !rightValue) return -1;
  return leftValue.localeCompare(rightValue);
}

export function formatAdminDate(value: string | null | undefined): string {
  if (!value) return "未設定";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}

export function formatPrimaryContact(familyAccount: FamilyAccountDto): string {
  return familyAccount.primaryContactDisplayNameSnapshot ?? "代表保護者（連絡先）未設定";
}

export function formatRosterPrimaryContact(
  primaryContact: { displayName: string; memberStatus: MemberStatus | null } | null
): string {
  return primaryContact?.displayName ?? "代表保護者（連絡先）未設定";
}

export function formatGuardianNames(guardians: readonly { displayName: string }[]): string {
  return guardians.map((guardian) => guardian.displayName).join("／");
}

export function summarizeNames(
  names: readonly string[],
  maxInline = 2
): { shortText: string; fullText: string } {
  const filtered = names.filter((name) => name.length > 0);
  const fullText = filtered.join("、");

  if (filtered.length === 0) {
    return { shortText: "", fullText: "" };
  }

  if (filtered.length <= maxInline) {
    return { shortText: fullText, fullText };
  }

  return {
    shortText: `${filtered.slice(0, maxInline).join("、")}ほか${filtered.length - maxInline}名`,
    fullText
  };
}

function uniqueStrings(values: readonly string[]): string[] {
  return [...new Set(values.filter((value) => value.length > 0))];
}

function isCurrentEnrollment(enrollment: EnrollmentDto): boolean {
  return CURRENT_ENROLLMENT_STATUSES.includes(enrollment.status as (typeof CURRENT_ENROLLMENT_STATUSES)[number]);
}

export function buildStudentRows(params: {
  students: readonly AdminStudentDto[];
  familyAccounts: readonly FamilyAccountDto[];
  enrollments: readonly EnrollmentDto[];
  relations?: AdminRosterRelationsResponseDto | null;
}): AdminStudentListRow[] {
  const familyById = new Map(params.familyAccounts.map((familyAccount) => [familyAccount.familyAccountId, familyAccount]));
  const relationByStudentId = new Map((params.relations?.studentRelations ?? []).map((relation) => [relation.studentId, relation]));
  const currentEnrollmentsByStudent = new Map<string, EnrollmentDto[]>();

  for (const enrollment of params.enrollments.filter(isCurrentEnrollment)) {
    const current = currentEnrollmentsByStudent.get(enrollment.studentId) ?? [];
    current.push(enrollment);
    currentEnrollmentsByStudent.set(enrollment.studentId, current);
  }

  return params.students.map((student) => {
    const currentEnrollments = currentEnrollmentsByStudent.get(student.studentId) ?? [];
    const currentLessonIds = uniqueStrings(currentEnrollments.map((enrollment) => enrollment.lessonId));
    const currentLessonTitles = uniqueStrings(currentEnrollments.map((enrollment) => enrollment.lessonTitle));
    const familyAccount = student.familyAccountId ? familyById.get(student.familyAccountId) ?? null : null;
    const relation = relationByStudentId.get(student.studentId);
    const siblingStudents = relation?.siblingStudents ?? [];
    const linkedGuardians = relation?.linkedGuardians ?? [];
    const primaryContact = relation?.primaryContact
      ? {
          displayName: relation.primaryContact.displayName,
          memberStatus: relation.primaryContact.memberStatus,
          hasActiveLinkToThisStudent: relation.primaryContact.hasActiveLinkToThisStudent
        }
      : null;
    const dataQualityWarnings = [
      student.familyAccountId && !familyAccount ? "Student references a missing FamilyAccount." : null,
      primaryContact && primaryContact.memberStatus === null ? "FamilyAccount primary contact has no member record." : null,
      primaryContact && !primaryContact.hasActiveLinkToThisStudent
        ? "FamilyAccount primary contact is not actively linked to this student."
        : null,
      ...linkedGuardians
        .filter((guardian) => guardian.memberStatus === "inactive")
        .map((guardian) => `${guardian.displayName} is an inactive member with an active guardian link.`),
      ...linkedGuardians
        .filter((guardian) => guardian.memberStatus === null)
        .map((guardian) => `${guardian.displayName} has an active guardian link but no member record.`)
    ].filter((value): value is string => typeof value === "string");

    return {
      student,
      familyAccount,
      siblingStudents,
      linkedGuardians,
      primaryContact,
      dataQualityWarnings,
      currentEnrollments,
      currentLessonIds,
      currentLessonTitles,
      searchText: buildAdminSearchHaystack([
        student.displayName,
        student.familyName,
        student.givenName,
        student.displayNameKana,
        student.memberCode,
        familyAccount?.displayName,
        ...siblingStudents.map((sibling) => sibling.displayName),
        ...linkedGuardians.map((guardian) => guardian.displayName),
        primaryContact?.displayName,
        ...currentLessonTitles
      ])
    };
  });
}

export function buildFamilyAccountRows(params: {
  familyAccounts: readonly FamilyAccountDto[];
  students: readonly AdminStudentDto[];
  enrollments: readonly EnrollmentDto[];
  relations?: AdminRosterRelationsResponseDto | null;
}): AdminFamilyAccountListRow[] {
  const studentsByFamily = new Map<string, AdminStudentDto[]>();
  const currentEnrollmentsByStudent = new Map<string, EnrollmentDto[]>();
  const relationByFamilyAccountId = new Map(
    (params.relations?.familyAccountRelations ?? []).map((relation) => [relation.familyAccountId, relation])
  );

  for (const student of params.students) {
    if (!student.familyAccountId) continue;
    const current = studentsByFamily.get(student.familyAccountId) ?? [];
    current.push(student);
    studentsByFamily.set(student.familyAccountId, current);
  }

  for (const enrollment of params.enrollments.filter(isCurrentEnrollment)) {
    const current = currentEnrollmentsByStudent.get(enrollment.studentId) ?? [];
    current.push(enrollment);
    currentEnrollmentsByStudent.set(enrollment.studentId, current);
  }

  return params.familyAccounts.map((familyAccount) => {
    const students = studentsByFamily.get(familyAccount.familyAccountId) ?? [];
    const currentEnrollments = students.flatMap((student) => currentEnrollmentsByStudent.get(student.studentId) ?? []);
    const currentLessonIds = uniqueStrings(currentEnrollments.map((enrollment) => enrollment.lessonId));
    const currentLessonTitles = uniqueStrings(currentEnrollments.map((enrollment) => enrollment.lessonTitle));
    const relation = relationByFamilyAccountId.get(familyAccount.familyAccountId);
    const linkedGuardians = relation?.linkedGuardians ?? [];
    const primaryContact = relation?.primaryContact
      ? {
          displayName: relation.primaryContact.displayName,
          memberStatus: relation.primaryContact.memberStatus,
          hasActiveLinkToAnyFamilyStudent: relation.primaryContact.hasActiveLinkToAnyFamilyStudent
        }
      : null;
    const dataQualityWarnings = [
      students.length === 0 ? "FamilyAccount has no students." : null,
      primaryContact && primaryContact.memberStatus === null ? "FamilyAccount primary contact has no member record." : null,
      primaryContact && !primaryContact.hasActiveLinkToAnyFamilyStudent
        ? "FamilyAccount primary contact is not actively linked to any family student."
        : null,
      ...linkedGuardians
        .filter((guardian) => guardian.memberStatus === "inactive")
        .map((guardian) => `${guardian.displayName} is an inactive member with an active guardian link.`),
      ...linkedGuardians
        .filter((guardian) => guardian.memberStatus === null)
        .map((guardian) => `${guardian.displayName} has an active guardian link but no member record.`)
    ].filter((value): value is string => typeof value === "string");

    return {
      familyAccount,
      students,
      linkedGuardians,
      primaryContact,
      dataQualityWarnings,
      currentLessonIds,
      currentLessonTitles,
      searchText: buildAdminSearchHaystack([
        familyAccount.displayName,
        formatRosterPrimaryContact(primaryContact),
        ...linkedGuardians.map((guardian) => guardian.displayName),
        ...students.map((student) => student.displayName),
        ...currentLessonTitles
      ])
    };
  });
}

export function buildLessonFilterOptions(lessons: readonly AdminLessonListItemDto[]) {
  return lessons
    .map((lesson) => ({ lessonId: lesson.lessonId, title: lesson.title }))
    .sort((left, right) => compareAdminText(left.title, right.title));
}
